import { useThemeStore } from '@sentinel/web-core';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { IdentifyResponse } from '@sentinel/shared';
import { createTerritory, sceneNode, type Territory } from './territory';

export type RecognisedOrganization = NonNullable<IdentifyResponse['organization']>;

const PIN_OFFSET = 34;

function usePinPosition(): { x: number; y: number } {
  const place = () => {
    const node = sceneNode(window.innerWidth, window.innerHeight);
    return { x: node.x, y: node.y - PIN_OFFSET };
  };
  const [position, setPosition] = useState(place);
  useEffect(() => {
    const onResize = () => setPosition(place());
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return position;
}

/** Decorative territory behind the desk (aria-hidden): the name of the recognised organization
 *  is also written in the card, the pin only echoes it on the map. */
export function TerritoryScene({ organization }: { organization: RecognisedOrganization | null }) {
  const { t } = useTranslation();
  const canvas = useRef<HTMLCanvasElement>(null);
  const territory = useRef<Territory | null>(null);
  const mode = useThemeStore((s) => s.mode);
  const pin = usePinPosition();

  useEffect(() => {
    if (canvas.current === null) return undefined;
    const created = createTerritory(canvas.current);
    territory.current = created;
    const scheme = window.matchMedia('(prefers-color-scheme: dark)');
    const repaint = () => created.repaint();
    scheme.addEventListener('change', repaint);
    return () => {
      scheme.removeEventListener('change', repaint);
      created.destroy();
      territory.current = null;
    };
  }, []);

  useEffect(() => territory.current?.repaint(), [mode]);
  useEffect(() => territory.current?.focus(organization !== null), [organization]);

  const kind = organization?.kind ?? 'other';
  return (
    <div className="acc-scene" aria-hidden="true">
      <canvas ref={canvas} className="acc-map" />
      <div
        className={['acc-pin', organization === null ? '' : 'on'].join(' ').trim()}
        style={{ left: pin.x, top: pin.y }}
      >
        <div className="tag">
          <b>{organization?.name ?? ''}</b>
          <small>{t(`accounts.scene.kind.${kind}`, t('accounts.scene.kind.other'))}</small>
        </div>
      </div>
    </div>
  );
}
